// External dependencies
import React, { Component, Fragment } from 'react';
import cloneDeep from 'lodash.clonedeep'
import { connect } from 'react-redux';

// Internal dependencies
import FormField from './CustomBenchmarkFormField';
import GradeCutlinesFormBlock from './GradeCutlinesFormBlock';
import { customBenchmarkFormSettings } from 'Constants/adminConstants';
import {
  setCustomBenchmark,
  getCustomBenchmark,
  getPlaceholderDefaultBenchmarkForm
} from 'Services/customBenchmarkService';
import teacherCreators from '../../../Reports/state/teacher/creators';
import {getMetrics} from "Components/Reports/values/metrics";

// Text
// TODO: Abstract text, once we've developed a consistent approach
const benchmarkFormText = {
  saveDataText: "Save",
  benchmarkStatusHeader: "Benchmark Status",
  benchmarkEnabledText: "enabled",
  benchmarkDisabledText: "disabled",
  pageIntro: "Use this form to enter in a custom benchmark for your district, with up to three cutlines. A valid cutline requires values for every season and grade. Your distict's custom benchmark is currently ",
}

// TODO dedup scale definitions
const scales = {
  WCPM:'Words Correct Per Minute (WCPM)',
  Lexile:'Lexile measure (based on grade equivalency)',
  DRA:'Developmental Reading Assessment',
};

const formErrorText = {
  nan: "ERROR: All benchmark values must be numbers.",
  incompleteCutline: "ERROR: Each cutline must be either complete, or completely empty. Values are needed for every grade and season.",
  empty: "ERROR: you must enter at least one cutline prior to enabling your custom benchmark.",
};

class CustomBenchmarkForm extends Component {
  constructor (props) {
    super(props);
    this.state = {
      form: null,
      customBenchmarkEnabled: false,
      formValidationError: null,
      initializedBenchmark: false,
      currentBenchmarksConfig: null,
    }
    this.handleCutlineValue = this.handleCutlineValue.bind(this);
    this.handleCutlineLabel = this.handleCutlineLabel.bind(this);
    this.handleToggleEnabledRequest = this.handleToggleEnabledRequest.bind(this);
    this.saveData = this.saveData.bind(this);
    this.fetchCustomBenchmarksForDistrict = this.fetchCustomBenchmarksForDistrict.bind(this);
  }

  componentDidMount() {
    if(this.props.district && (this.props.selectedMetric && !["ORF", "ARM"].includes(this.props.selectedMetric.value) || !this.state.form)){
      return this.fetchCustomBenchmarksForDistrict(this.props.district.id);
    }
  }

  componentDidUpdate() {
    if(this.props.district) {
      if (!this.state.initializedBenchmark && !this.state.form) {
        this.setState({
          initializedBenchmark: true,
        })
        return this.fetchCustomBenchmarksForDistrict(this.props.district.id);
      }
      else if (this.state.form && this.state.form.meta.scale !== this.props.selectedScale.value){
        let form;
        let customBenchmarkEnabled;
        if(this.props.selectedScale.value === this.state.currentBenchmarksConfig.meta.scale){
          form = cloneDeep(this.state.currentBenchmarksConfig);
          customBenchmarkEnabled = this.state.currentBenchmarksConfig.meta.enabled;
        } else {
          form = getPlaceholderDefaultBenchmarkForm(this.props.district.id)
          form.meta.scale = this.props.selectedScale.value;
          customBenchmarkEnabled = false;
        }

        this.setState({
          form,
          customBenchmarkEnabled,
          formValidationError: null,
        });
      }
    }
  }

  fetchCustomBenchmarksForDistrict(districtId){
    return getCustomBenchmark(districtId).then(data => {
      this.setState({
        form: data,
        currentBenchmarksConfig: data,
        customBenchmarkEnabled: data.meta.enabled,
      });

      const availableMetrics = getMetrics();
      let metricId = data.meta.scale === 'WCPM' ? 'ORF' :  data.meta.scale;

      this.props.teacherSelect({
          type: 'metric',
          data: availableMetrics.find(metric => metric.value === metricId),
        },
        this.props.district,
        [],// null students
      );
    });
  }

  async saveData() {
    await setCustomBenchmark(this.state.form, this.props.selectedScale, this.props.district);
    this.setState({
      currentBenchmarksConfig: cloneDeep(this.state.form)
    });
  }

  // Update a cutline value within the form
  handleCutlineValue(input, id) {
    // update the field
    let newFormValues = this.state.form;
    newFormValues.cutlines[id.cutline].grades[id.grade].seasons[id.season].value = input;

    // validate the form, ignoring "incomplete cutline errors for now"
    let {validCutlinesData, nanFound, partiallyCompletedCutline, formHasUserData} = validateForm(this.state.form);
    let formValidationError = null;
    if(!validCutlinesData && nanFound) {
      let formValidationError = formErrorText.nan;
    } else if (!validCutlinesData && !formHasUserData) {
      let formValidationError = formErrorText.empty;
    }

    this.setState({
      form: newFormValues,
      formValidationError,
    });
  }

  // Update cutline label within the form
  handleCutlineLabel(input, id) {
    let newFormValues = this.state.form;
    newFormValues.cutlines[id].label = input;
    this.setState({
      form: newFormValues,
    });
  }

  /*
    Handles a request to enable or disable the custom benchmark.
    If currently enabled, disables.
    If currently disabled, performs form validation, and then enables or displays error message
  */
  handleToggleEnabledRequest() {
    const enabled = !this.state.form.meta.enabled;
    let newFormValues = {
      ...this.state.form,
      meta: {...this.state.form.meta, enabled}
    };

    // Validate the form data and, if there is invalid form data, display the appropriate error
    let {validCutlinesData, nanFound, partiallyCompletedCutline, formHasUserData} = validateForm(this.state.form);
    if(enabled && !validCutlinesData) { // If the form is invalid, show the appropriate error
      let errorMessage;
      if(nanFound) {
        errorMessage = formErrorText.nan;
      } else if(!formHasUserData) {
        errorMessage = formErrorText.empty;
      } else {
        errorMessage = formErrorText.incompleteCutline;
      }
      this.setState({
        formValidationError: errorMessage,
      });
    } else { // Otherwise, submit the valid form and clear errors
      setCustomBenchmark(newFormValues, this.props.selectedScale, this.props.district);

      // Update the UI
      this.setState({
        formValidationError: null,
        customBenchmarkEnabled: enabled,
        form: newFormValues,
        currentBenchmarksConfig: cloneDeep(newFormValues),
      });
    }
  }

  render () {
    // TODO shorten render method https://amiralearning.atlassian.net/browse/AE-1282

    // Benchmark toggle text
    let benchmarkEnableToggleText = benchmarkFormText.benchmarkDisabledText;
    if(this.state.customBenchmarkEnabled) {
      benchmarkEnableToggleText = benchmarkFormText.benchmarkEnabledText;
    }

    // TODO refactor to use less (or no) fragments https://amiralearning.atlassian.net/browse/AE-1281
    return (
      <div className="customBenchmarkFormContainer">
       {this.state.form && <Fragment>
          <div className="customBenchmarkDescription">
            {benchmarkFormText.pageIntro}
            {benchmarkEnableToggleText}.
          </div>
          <div className="toggleHeader"> {benchmarkFormText.benchmarkStatusHeader} </div>
          <div className="customBenchmarkToggle">
            <div
              onClick={this.handleToggleEnabledRequest}
              className="smallToggle">
              <input
                className="pillInput"
                type="checkbox"
                onChange={()=>{}}
                checked={this.state.customBenchmarkEnabled}
              />
              <label className="pillLabel" htmlFor="switch"></label>
            </div>
            <div className="toggleLabel">{benchmarkEnableToggleText}</div>
            {this.state.formValidationError && <div className="validationError"> {this.state.formValidationError} </div>}
          </div>
          <div className="formHeader">
              <div className="formLabel cutlineLabelHeader">Cutline Labels</div>
              <div className="formLabel gridSpacerDiv">{/*empty div for css grid styling*/}</div>
              <FormField
                placeholder='Catch Up'
                onReceiveUserInput={this.handleCutlineLabel}
                id={this.state.form.cutlines[0].id}
                value={this.state.form.cutlines[0].label == 'Cutline 1' || this.state.form.cutlines[0].label == 'Catch Up' ? '' : this.state.form.cutlines[0].label}/>
              <FormField
                placeholder="On Track"
                onReceiveUserInput={this.handleCutlineLabel}
                id={this.state.form.cutlines[1].id}
                value={this.state.form.cutlines[1].label == 'Cutline 2' || this.state.form.cutlines[0].label == 'On Track' ? '' : this.state.form.cutlines[1].label}/>
              <FormField
                placeholder="At/Above"
                onReceiveUserInput={this.handleCutlineLabel}
                id={this.state.form.cutlines[2].id}
                value={this.state.form.cutlines[2].label == 'Cutline 3' || this.state.form.cutlines[0].label == 'At/Above' ? '' : this.state.form.cutlines[2].label}/>
          </div>
          <div className="formWrapper">
            <div
              className="customBenchmarkForm">
              {customBenchmarkFormSettings.grades.map(grade => {
                let gradeCutlineValues  = this.state.form.cutlines.map(cutline => {
                  return cutline.grades[grade.value];
                });
                return <GradeCutlinesFormBlock
                  key={grade.value}
                  units={this.props.selectedScale.value}
                  grade={grade}
                  values={gradeCutlineValues}
                  onReceiveUserInput={this.handleCutlineValue}
                  form={this.state.form}
                />})}
            </div>
          </div>
          <div className="customBenchmarkFormControls">
            <button className="primaryTeacherButton right" onClick={this.saveData}>{benchmarkFormText.saveDataText}</button>
          </div>
        </Fragment>}
      </div>
    )
  }
}

// Validate the form
// TODO catch other classes of error, abstract this into a service as it grows, or otherwise figure out where to put it
function validateForm(formData) {
  let validCutlinesData = true;
  let nanFound = false;
  let partiallyCompletedCutline = false;
  let formHasUserData = false;
  // For each form field, check for valid data.
  // For each cutline, check for completeness
  for(let z = 0; z < formData.cutlines.length; z++) {
    let cutline = formData.cutlines[z];
    for(let i = 0; i < cutline.grades.length; i++) {
      let cutlineHasValues = false;
      let cutlineHasEmptyFields = false;
      for(let j = 0; j < cutline.grades[i].seasons.length; j++) {
        let fieldValue = cutline.grades[i].seasons[j].value;
        if(fieldValue != '' && isNaN(parseFloat(fieldValue))) {
          nanFound = true;
          validCutlinesData = false;
          break;
        }
        if(fieldValue !== '') {
          cutlineHasValues = true;
          formHasUserData = true;
        } else {
          cutlineHasEmptyFields = true;
        }
      }
      if(cutlineHasValues && cutlineHasEmptyFields) {
        partiallyCompletedCutline = true;
        validCutlinesData = false;
        break;
      }
    }
    if(!validCutlinesData) {
      break;
    }
  }
  // if the user hasn't entered any data, the cutline data isn't validated
  // (can't be enabled)
  if(!formHasUserData) {
    validCutlinesData = false;
  }
  return {validCutlinesData, nanFound, partiallyCompletedCutline, formHasUserData};
}

const mapStateToProps = (state) => {return {}};

const mapDispatchToProps = (dispatch) => ({
  teacherSelect: (selection, district, students) => dispatch(teacherCreators.teacherSelect(selection, district, students)),
});


export default connect(mapStateToProps, mapDispatchToProps)(CustomBenchmarkForm);